import React from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const StyledAdvancedTabs = styled.div({
    width: "100%",
    alignSelf: "stretch",
    marginBottom: tokens.globals["spacing-6"],
    borderBottom: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
});

const StyledItems = styled.div({
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: tokens.globals["spacing-1"],
    flexWrap: "wrap",
});

const StyledTabItem = styled.button(({ $active = false }) => ({
    height: "26px",
    padding: `${tokens.globals["spacing-0-5"]} ${tokens.globals["spacing-1-5"]} ${tokens.globals["spacing-1"]}`,
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    gap: tokens.globals["spacing-1"],
    background: "transparent",
    border: "none",
    borderBottom: $active
        ? `${tokens.globals["spacing-0-5"]} solid ${tokens.colors.theme["sidebar-color"]}`
        : `${tokens.globals["spacing-0-5"]} solid transparent`,
    color: tokens.colors.text.primary,
    opacity: $active ? 1 : 0.6,
    cursor: "pointer",
    transition: "opacity 0.2s ease-in-out, border-color 0.2s ease-in-out",
    userSelect: "none",
    fontFamily: tokens.typography.fontFamilies["albert-sans"],

    "&:hover:not(:disabled)": {
        opacity: 1,
    },
    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-0-5"],
        borderRadius: tokens.globals["border-radius"],
    },
    "&:disabled": {
        opacity: 0.4,
        cursor: "not-allowed",
    },
}));

const StyledTabDivider = styled.div({
    width: tokens.globals["spacing-px"],
    height: tokens.globals["spacing-5"],
    flexShrink: 0,
    background: tokens.colors.surfaces["border-divider"],
});

const StyledLeftIcon = styled.span({
    width: tokens.globals["spacing-4"],
    height: tokens.globals["spacing-4"],
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
});

const StyledIcon = styled.i({
    color: "currentColor",
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "14px",
});

const StyledLabel = styled.span({
    color: "currentColor",
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "20px",
    whiteSpace: "nowrap",
});

function TabItem({ icon, label, active = false, onClick, showDivider = false, disabled = false, title }) {
    return (
        <>
            {showDivider && <StyledTabDivider aria-hidden="true" />}
            <StyledTabItem
                type="button"
                role="tab"
                $active={active}
                aria-selected={active}
                disabled={disabled}
                onClick={onClick}
                title={title}
            >
                {icon && (
                    <StyledLeftIcon aria-hidden="true">
                        <StyledIcon className={`fal fa-${icon}`} />
                    </StyledLeftIcon>
                )}
                <StyledLabel>{label}</StyledLabel>
            </StyledTabItem>
        </>
    );
}

/**
 * Reusable AIE tab navigation bar.
 * @param {Array<object>} [props.tabs=[]] - Tab definitions, rendered in order. `showDivider`
 *   renders a vertical divider before that tab.
 * @param {string} [props.className] - Optional class name.
 * @param {string} [props.ariaLabel="Section tabs"] - Accessible label for the tablist.
 */
export function AdvancedTabs({ tabs = [], className, ariaLabel = "Section tabs" }) {
    return (
        <StyledAdvancedTabs className={className}>
            <StyledItems role="tablist" aria-label={ariaLabel}>
                {tabs.map((tab, index) => (
                    <TabItem
                        key={tab.key || `${tab.label || "tab"}-${index}`}
                        icon={tab.icon}
                        label={tab.label}
                        active={Boolean(tab.active)}
                        onClick={tab.onClick}
                        showDivider={Boolean(tab.showDivider)}
                        disabled={Boolean(tab.disabled)}
                        title={tab.title}
                    />
                ))}
            </StyledItems>
        </StyledAdvancedTabs>
    );
}

AdvancedTabs.displayName = "AdvancedTabs";

export default AdvancedTabs;
